import React from 'react';
import { useNavigate } from 'react-router-dom';

import { UserAvatar } from '@bonusly/common-user-popover';
import { Box, Flex, Text } from '@bonusly/design-system';

import { useCelebrationDisplay } from '../../../../components/celebrations/celebrations-widget/hooks/use-celebration-display';
import { useTrackCelebrations } from '../../../../components/celebrations/celebrations-widget/hooks/use-track-celebrations';
import { MonthlyCelebration } from '../../../../components/celebrations/types';

interface PanelCelebrationItemProps {
	celebration: MonthlyCelebration;
	onClose: () => void;
}

export const PanelCelebrationItem = ({ celebration, onClose }: PanelCelebrationItemProps): React.ReactElement => {
	const { created_at, date, display_name, id, type, user_id } = celebration;

	const { emoji, text } = useCelebrationDisplay(type, created_at, date);
	const navigate = useNavigate();
	const { trackCelebrationModalLinkClick } = useTrackCelebrations();

	const handleClick = () => {
		if (id) {
			trackCelebrationModalLinkClick({ celebrationType: type });
			onClose();
			navigate(`/bonuses/${id}`);
		}
	};

	const isClickable = !!id;

	return (
		<Box
			as={isClickable ? 'button' : 'div'}
			data-testid={`panel-celebration-item-${id ?? user_id}`}
			listStyleType="none"
			borderRadius="md"
			p={3}
			maxW="100%"
			w="100%"
			textAlign="left"
			cursor={isClickable ? 'pointer' : 'default'}
			transition="background 0.15s ease"
			_hover={isClickable ? { bgColor: 'bg.subtle' } : undefined}
			onClick={isClickable ? handleClick : undefined}
		>
			<Flex alignItems="center" gap={3}>
				<UserAvatar userId={user_id} />
				<Box minW={0} flex={1}>
					<Text color="text.default" fontSize={100} fontWeight="medium" lineHeight={6} mb={0} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
						{display_name}
					</Text>
					<Text color="text.softest" fontSize={50} fontWeight="normal" lineHeight={5}>
						{emoji} {text}
					</Text>
				</Box>
			</Flex>
		</Box>
	);
};
